import React, { useState, useEffect } from "react";
import {
  Drawer,
  Toolbar,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Button,
  Card,
  CardContent,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import * as MuiIcons from "@mui/icons-material";
import navItems from "../data/navItems.json";
import { Domain } from "../features/domain/models/Domain";
import { domainStorage } from "../features/domain/utils/domainStorage";
import { useStoredDomain } from "../features/domain/hooks/useStoredDomain";

const drawerWidth = 240;

const getMuiIcon = (iconName?: string) => {
  if (!iconName) return null;
  const IconComponent = MuiIcons[iconName as keyof typeof MuiIcons];
  return IconComponent ? <IconComponent fontSize="small" /> : null;
};

const Sidebar = () => {
  const location = useLocation();
  const [open, setOpen] = useState<Record<number, boolean>>({});

  const selectedDomain = useStoredDomain();

  const toggleSubMenu = (index: number) => {
    setOpen((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleUnselectDomain = () => {
    domainStorage.clear();
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundColor: "background.paper",
          borderRight: "1px solid",
          borderColor: "divider",
        },
      }}
    >
      <Toolbar />
      <Box sx={{ textAlign: "center", p: 1 }}>
        <img
          src="/assets/img/multi-domain-cms_2_0.png"
          alt="Logo"
          style={{ width: "100%", maxWidth: 200 }}
        />

        {selectedDomain && (
          <Card
            variant="outlined"
            sx={{
              mt: 2,
              mb: 1,
              backgroundColor: (theme) => theme.palette.background.default,
            }}
          >
            <CardContent>
              <Typography
                variant="body2"
                fontWeight="bold"
                noWrap
                sx={{ px: 1 }}
              >
                {selectedDomain.id} - {selectedDomain.name}
              </Typography>
              <Button
                size="small"
                onClick={handleUnselectDomain}
                color="error"
                fullWidth
                variant="outlined"
                sx={{ mt: 0.5 }}
              >
                Unselect
              </Button>
            </CardContent>
          </Card>
        )}
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <List>
          {navItems.map((item, index) => (
            <Box key={index}>
              <ListItemButton
                component={item.subItems ? "div" : Link}
                to={!item.subItems ? item.href : undefined}
                onClick={() => item.subItems && toggleSubMenu(index)}
                selected={location.pathname === item.href}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {getMuiIcon(item.icon)}
                </ListItemIcon>
                <ListItemText primary={item.label} />
                {item.subItems &&
                  (open[index] ? <ExpandLess /> : <ExpandMore />)}
              </ListItemButton>

              {item.subItems && (
                <Collapse in={open[index]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.subItems.map((subItem, subIdx) => (
                      <ListItemButton
                        key={subIdx}
                        component={Link}
                        to={subItem.href}
                        sx={{ pl: 6 }}
                        selected={location.pathname === subItem.href}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {getMuiIcon(subItem.icon)}
                        </ListItemIcon>
                        <ListItemText primary={subItem.label} />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </Box>
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
